import type { MarkingColor } from './schema'
import { markingColorStyle } from './schema'
import { findCitationParent, isNoiseTextNode } from './text-filter'

const MARK_SELECTOR = 'mark[data-marking-id]'

function markSelectorFor(id: string): string {
  return `mark[data-marking-id="${CSS.escape(id)}"]`
}

/** Every `<mark>` belonging to one marking, in document order. */
export function markElementsFor(id: string): NodeListOf<HTMLElement> {
  return document.querySelectorAll<HTMLElement>(markSelectorFor(id))
}

export function allMarkElements(): NodeListOf<HTMLElement> {
  return document.querySelectorAll<HTMLElement>(MARK_SELECTOR)
}

export function hasMarkElementIn(root: ParentNode, id: string): boolean {
  return !!root.querySelector(markSelectorFor(id))
}

export function closestMarkElement(node: Element | null | undefined): HTMLElement | null {
  return node?.closest<HTMLElement>(MARK_SELECTOR) ?? null
}

interface MarkAttrs {
  id: string
  color: MarkingColor
}

/**
 * Wraps the contents of the given Range in one or more `<mark>` elements.
 * `surroundContents()` throws on ranges spanning multiple inline elements
 * (e.g. across a `<sup>` footnote marker), so those fall back to walking and
 * wrapping the individual text nodes.
 */
export function wrapRangeWithMark(range: Range, attrs: MarkAttrs): HTMLElement[] {
  const citationParent = findCitationParent(range.startContainer)

  // Fast path — still skip noise nodes (e.g. inside a `.reference` span).
  if (
    range.startContainer === range.endContainer
    && range.startContainer.nodeType === Node.TEXT_NODE
  ) {
    if (isNoiseTextNode(range.startContainer, citationParent)) {
      return []
    }

    const mark = createMarkElement(attrs)
    range.surroundContents(mark)
    return [mark]
  }

  return wrapRangeAcrossTextNodes(range, attrs, citationParent)
}

function wrapRangeAcrossTextNodes(
  range: Range,
  attrs: MarkAttrs,
  citationParent: Element | null,
): HTMLElement[] {
  const created: HTMLElement[] = []
  const root = range.commonAncestorContainer
  const walker = document.createTreeWalker(
    root.nodeType === Node.TEXT_NODE ? root.parentNode! : root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (isNoiseTextNode(node, citationParent)) {
          return NodeFilter.FILTER_REJECT
        }

        const nodeRange = document.createRange()
        nodeRange.selectNodeContents(node)
        const intersects =
          range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0
          && range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
        return intersects ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
      },
    },
  )

  const textNodes: Text[] = []
  let cursor: Node | null = walker.nextNode()

  while (cursor) {
    if (cursor instanceof Text) {
      textNodes.push(cursor)
    }

    cursor = walker.nextNode()
  }

  for (const node of textNodes) {
    const start = node === range.startContainer ? range.startOffset : 0
    const end = node === range.endContainer ? range.endOffset : node.length

    if (start === end) {
      continue
    }

    const sliceRange = document.createRange()
    sliceRange.setStart(node, start)
    sliceRange.setEnd(node, end)
    const mark = createMarkElement(attrs)
    sliceRange.surroundContents(mark)
    created.push(mark)
  }

  return created
}

/**
 * Retags a marking's `<mark>` elements in place — color swaps can't unwrap +
 * re-wrap because the original Range's endpoints were invalidated by the wrap.
 */
export function recolorMarkingDom(id: string, color: MarkingColor, elements: Iterable<HTMLElement> = markElementsFor(id)): void {
  const className = markingColorStyle(color).markClass

  for (const el of elements) {
    // Rehydration runs this on every pass, and rewriting an unchanged class
    // invalidates style for the whole container.
    if (el.dataset.markingColor !== color) {
      el.className = className
      el.dataset.markingColor = color
    }
  }
}

export function unwrapMarking(id: string): void {
  for (const el of markElementsFor(id)) {
    const parent = el.parentNode

    if (!parent) {
      continue
    }

    while (el.firstChild) {
      parent.insertBefore(el.firstChild, el)
    }

    parent.removeChild(el)
    parent.normalize()
  }
}


function createMarkElement(attrs: MarkAttrs): HTMLElement {
  const mark = document.createElement('mark')
  mark.className = markingColorStyle(attrs.color).markClass
  mark.dataset.markingId = attrs.id
  mark.dataset.markingColor = attrs.color
  return mark
}

/** Range spanning all of a marking's `<mark>` elements, in document order. */
export function markingDomRange(id: string): Range | null {
  const elements = markElementsFor(id)
  const first = elements[0]
  const last = elements[elements.length - 1]

  if (!first || !last) {
    return null
  }

  const range = document.createRange()
  range.setStartBefore(first)
  range.setEndAfter(last)
  return range
}
